import { Customer } from "definitions/Customer"
import { Tooltip } from "@material-ui/core"
import React, { useEffect, useState } from "react"
import Modal from "react-modal"
import { getRequest, postRequest } from "utils/request"
import { useHistory } from "react-router-dom"
import { translate } from "translate/i18n"
import { useNotification } from "hooks/notification"
import { useAuth } from "hooks/auth"
import { User } from "definitions/User"
import { toast } from "react-toastify"
import { Ticket } from "definitions/Ticket"

type TwilioTemplateList = {
  contents: TwilioTemplate[]
  meta: {
    first_page_url: string
    key: string
    next_page_url: string | null
    page: number
    page_size: number
    previous_page_url: string | null
    url: string
  }
}

type TwilioTemplate = {
  account_sid: string
  date_created: string
  date_updated: string
  friendly_name: string
  language: string
  links: {
    approval_create: string
    approval_fetch: string
  }
  sid: string
  types: {
    "twilio/quick-reply"?: {
      actions: Array<{
        id: string
        title: string
      }>
      body: string
    }
    "twilio/text"?: {
      body: string
    }
  }
  url: string
  variables: {
    [key: string]: string
  }
}

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "40%",
    minHeight: "30%",
    minWidth: "580px",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "stretch",
    padding: 0,
  },
  overlay: {
    overflow: "auto",
    transition: "(top 0.4s, opacity 0.4s)",
    zIndex: 1003,
    background: "rgba(0, 0, 0, 0.4)",
  },
}

type SendWhatsappMsgToCustomerModalProps = {
  customer: Customer
  isModalSendWhatsappMessageOpen: boolean
  closeModal: () => void
}

type TicketType = {
  id: number
  description: string
}

type InitiateChatDto = {
  customer: {
    id: number
    name: string
    phone: string
  }
  agent: {
    id: number
    name: string
  }
  company: {
    id: number
    name: string
  }
  templateSid: string
  ticketTypeId: number
}

function SendWhatsappMsgToCustomerModal(
  props: SendWhatsappMsgToCustomerModalProps
): JSX.Element {
  const { customer, isModalSendWhatsappMessageOpen, closeModal } = props
  const history = useHistory()
  const { selectedCompany, updateHeaderSelectedTicket } = useNotification()
  const { user } = useAuth() as { user: User }
  const [isPending, setIsPending] = useState(false)

  const [ticketTypesList, setTicketTypesList] = useState<TicketType[]>([])
  const [selectedTicketTypeId, setSelectedTicketTypeId] = useState<
    number | undefined
  >(undefined)

  const [twilioTemplates, setTwilioTemplates] = useState<TwilioTemplate[]>([])
  const [selectedTwilioTemplate, setSelectedTwilioTemplate] =
    useState<TwilioTemplate | null>(null)
  const [stayOnPage, setStayOnPage] = useState(false)

  const handleBeginCustomerChat = () => {
    const dto: InitiateChatDto = {
      customer: {
        id: customer.id,
        name: customer.name,
        phone: customer.cellPhone,
      },
      agent: {
        id: user.id,
        name: user.name,
      },
      company: {
        id: selectedCompany.id || 0,
        name: selectedCompany.nickName || "",
      },
      templateSid: selectedTwilioTemplate?.sid || "",
      ticketTypeId: selectedTicketTypeId || 0,
    }

    const startChat = async () => {
      setIsPending(true)
      const result = await postRequest<Ticket>("/chat/initiate", dto)
      setIsPending(false)
      if (result.success) {
        closeModal()
        toast.success(translate("messageSentToCustomer"))
        updateHeaderSelectedTicket(result.data.id, result.data)

        if (!stayOnPage) {
          setTimeout(() => {
            history.push("/dashboard/chat")
          }, 1000)
        }
      } else {
        setSelectedTwilioTemplate(null)
      }
    }

    startChat()
  }

  useEffect(() => {
    const getTemplates = async () => {
      setIsPending(true)
      const response = await getRequest<TwilioTemplateList>(
        `whatsappcallback/twilio-templates?CompanyId=${selectedCompany.id}`
      )
      setIsPending(false)
      if (response.success) {
        const templates = response.data
        setTwilioTemplates(templates.contents)
      }
    }

    getTemplates()
  }, [selectedCompany.id])

  useEffect(() => {
    const getTicketTypes = async () => {
      setIsPending(true)
      const response = await getRequest(
        `companiesticketstypes?CompanyId=${selectedCompany.id}&onlyActive=1`
      )
      setIsPending(false)
      if (response.success) {
        console.log("response.data companiesticketstypes", response.data)
        setTicketTypesList(response.data as TicketType[])
      }
    }

    getTicketTypes()
  }, [selectedCompany.id])

  return (
    <>
      {/* @ts-ignore */}
      <Modal
        isOpen={isModalSendWhatsappMessageOpen}
        // @ts-ignore
        style={customStyles}
        onRequestClose={closeModal}
        ariaHideApp={false}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
          }}
        >
          <div className="modal-header">
            <h4 className="modal-title">
              {translate("sendWhatsappMessageToCustomerModalTitle")}
              <span className="mr-1" />
              {customer.name && customer.name.length
                ? customer.name
                : "(sem nome cadastrado)"}
            </h4>
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
              onClick={closeModal}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {/* Shortcuts Select Input */}
            <div className="form-group">
              <label htmlFor="templates-selector">Templates</label>
              <select
                className="form-control"
                id="templates-selector"
                value={selectedTwilioTemplate?.sid || ""}
                onChange={(e) => {
                  const template = twilioTemplates.find(
                    (t) => t.sid === e.target.value
                  )
                  setSelectedTwilioTemplate(template || null)
                }}
                disabled={isPending}
              >
                <option value="" disabled>
                  {isPending
                    ? translate("loading")
                    : translate("selectTemplate")}
                </option>
                {twilioTemplates?.map((template) => (
                  <option value={template.sid} key={template.sid}>
                    {template.friendly_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Message Textarea */}
            <div className="form-group">
              <label htmlFor="messageTextarea">{translate("message")}</label>
              <textarea
                className="form-control"
                id="messageTextarea"
                rows={3}
                value={
                  selectedTwilioTemplate?.types["twilio/quick-reply"]?.body ||
                  selectedTwilioTemplate?.types["twilio/text"]?.body ||
                  ""
                }
                readOnly
                placeholder={translate("selectTemplateToSeeMessage")}
              ></textarea>
            </div>

            {/* Ticket Type Select Input */}
            <div className="form-group">
              <label htmlFor="ticketTypeSelect">
                {translate("ticketType")}
              </label>
              <select
                className="form-control"
                id="ticketTypeSelect"
                value={selectedTicketTypeId}
                onChange={(e) => {
                  setSelectedTicketTypeId(Number(e.target.value))
                }}
                disabled={isPending}
              >
                <option value={undefined} /* disabled */>
                  {isPending
                    ? translate("loading")
                    : translate("selectTicketType")}
                </option>
                {ticketTypesList.map((ticketType) => (
                  <option value={ticketType.id} key={ticketType.id}>
                    {ticketType.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Stay on Page Checkbox */}
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="stayOnPageCheckbox"
                checked={stayOnPage}
                onChange={(e) => setStayOnPage(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="stayOnPageCheckbox">
                {translate("stayOnThisPage")}
              </label>
            </div>
          </div>
          <div className="modal-footer">
            {/* Modal Action Buttons */}
            <button
              className="btn btn-secondary"
              onClick={closeModal}
              type="button"
              disabled={isPending}
            >
              {translate("toClose")}
            </button>
            <button
              className="btn btn-success"
              onClick={handleBeginCustomerChat}
              disabled={
                isPending ||
                !twilioTemplates.length ||
                !selectedTwilioTemplate ||
                !ticketTypesList.length ||
                !selectedTicketTypeId
              }
            >
              {translate("send")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

type SendWhatsappMsgToCustomerProps = {
  customer: Customer
}

export function SendWhatsappMsgToCustomer(
  props: SendWhatsappMsgToCustomerProps
) {
  const { customer } = props
  const [isModalSendWhatsappMessageOpen, setIsModalSendWhatsappMessageOpen] =
    useState(false)
  const openModal = () => {
    setIsModalSendWhatsappMessageOpen(true)
  }
  const closeModal = () => {
    setIsModalSendWhatsappMessageOpen(false)
  }

  const { selectedCompany } = useNotification()

  const [companyUsesTwilio, setCompanyUsesTwilio] = useState(true)

  useEffect(() => {
    const checkCompanyUsesTwilio = async () => {
      const whatsappSettingsResponse = await getRequest<{
        success: boolean
        source: string
      }>(`/companieschannels/settings/whatsapp?CompanyId=${selectedCompany.id}`)
      if (whatsappSettingsResponse.success) {
        setCompanyUsesTwilio(whatsappSettingsResponse.data.source === "Twilio")
      }
    }

    checkCompanyUsesTwilio()
  }, [selectedCompany.id])

  return (
    <>
      <Tooltip
        arrow
        title='Ative "Twilio" nas configurações e adicione um número de telefone a este cliente'
        disableHoverListener={Boolean(customer.cellPhone) && companyUsesTwilio}
      >
        <button
          style={{
            marginRight: 10,
            backgroundColor: "rgba(0,0,0,0)",
            border: 0,
            padding: 0,
          }}
          onClick={openModal}
          disabled={!customer.cellPhone || !companyUsesTwilio}
        >
          <i className="mdi mdi-whatsapp" style={{ fontSize: 20 }}></i>
        </button>
      </Tooltip>
      {isModalSendWhatsappMessageOpen && (
        <SendWhatsappMsgToCustomerModal
          isModalSendWhatsappMessageOpen={isModalSendWhatsappMessageOpen}
          closeModal={closeModal}
          customer={customer}
        />
      )}
    </>
  )
}
